import styles from './page.module.css';

import get from 'lodash/get';
import Head from 'next/head';
import { MutableRefObject, ReactElement, useContext, useEffect } from 'react';

import { COOKIE_HIDE_NAME } from 'core/constants/default-values';
import { SearchOptions } from 'core/entities/search';
import { CommonPageContext } from 'core/services/context/context';
import { isSafeUrlParamStrict } from 'core/services/safe-url';
import { saveUtm } from 'core/utils/metrics';

import { CommonContext } from 'contexts/common';

import { HeaderBackLink, HeaderBackLinkProps } from 'components/layout/header-back-link/header-back-link';
import { Layout } from 'components/layout/layout';

interface PageProps {
  ctx: CommonPageContext;
  children?: ReactElement;

  renderLayout?(): ReactElement;

  backLink?: Optional<HeaderBackLinkProps>;
  hasSearchBar?: boolean;

  searchOptions?: SearchOptions;

  slimFooter?: boolean;
  noHeader?: boolean;
  noFooter?: boolean;
  noIndex?: boolean;
  isCentral?: boolean;
  isTransparentHead?: boolean;
  hasMobileSearchBar?: boolean;
  withoutLoading?: boolean;
  simpleHeader?: boolean;
  disableHorizontalMobileScroll?: boolean;
  imageUrl?: string;
  headerClassName?: string;

  headerRef?: MutableRefObject<Optional<HTMLDivElement>>;
}

const extraHtml =
  'window.dataLayer = {push: function (params) {}};window.ga=function(p1,p2,p3){};' +
  '(function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};' +
  'm[i].l=1*new Date();' +
  'for (var j = 0; j < document.scripts.length; j++) {if (document.scripts[j].src === r) { return; }}' +
  'k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})' +
  '(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");' +
  'ym(17911543, "init", {clickmap:true,trackLinks:true,accurateTrackBounce:true,webvisor:true,ecommerce:"dataLayer"});';

// eslint-disable-next-line complexity
const SeoHead = (props: PageProps) => {
  const image = props.imageUrl ? props.imageUrl : '/s/modern/logo/kvartirka-logo-vk.png';
  return (
    <Head>
      <title>{get(props.ctx, 'seo.title', '')}</title>
      <meta property='og:type' content='website' />
      <meta property='og:title' content={get(props.ctx, 'seo.title', '')} key='title' />
      <meta property='og:description' content={get(props.ctx, 'seo.description', '')} key='description' />
      <meta property='og:url' content={get(props.ctx, 'seo.canonical', '')} key='canonical' />
      <meta property='og:image' content={image} key='image' />
      <meta name='description' lang='ru' content={get(props.ctx, 'seo.description', '')} />
      <meta name='yandex-tableau-widget' content='/s/modern/manifest/yandex-tableau-widget.png, color=#ffffff' />
      <meta name='yandex-verification' content='7a1417b82eed880a' />
      <meta name='google-site-verification' content='lXIZ9WjBcAgIMSFCrGl-gVlOrJIeNp7qkMtTAblMqQo' />
      // TODO: Получать id приложений с сервера
      <meta name='apple-itunes-app' content='app-id=733790235' />
      <meta name='google-play-app' content='app-id=ru.kvartirka.android_new' />
      {props.noIndex ? <meta name='robots' content='noindex, follow' /> : null}
      {props.imageUrl && <link rel='image_src' href={props.imageUrl} />}
      <link type='image/x-icon' rel='shortcut icon' href='/favicon.ico' />
      <link type='image/png' rel='icon' sizes='16x16' href='/favicon-16x16.png' />
      <link type='image/png' rel='icon' sizes='32x32' href='/favicon-32x32.png' />
      <link type='image/png' rel='icon' sizes='48x48' href='/favicon-48x48.png' />
      <link type='image/png' rel='icon' sizes='64x64' href='/favicon-64x64.png' />
      <link type='image/png' rel='icon' sizes='128x128' href='/favicon-128x128.png' />
      <link type='image/png' rel='icon' sizes='256x256' href='/favicon-256x256.png' />
      <link type='image/svg+xml' rel='icon' href='/favicon.svg' />
      <link
        type='image/png'
        rel='apple-touch-icon-precomposed'
        href='/s/modern/apple-touch/common/apple-touch-icon-precomposed.png'
      />
      <link
        type='image/png'
        rel='apple-touch-icon-precomposed'
        sizes='72x72'
        href='/s/modern/apple-touch/common/apple-touch-icon-72x72-precomposed.png'
      />
      <link
        type='image/png'
        rel='apple-touch-icon-precomposed'
        sizes='114x114'
        href='/s/modern/apple-touch/common/apple-touch-icon-114x114-precomposed.png'
      />
      <link
        type='image/png'
        rel='apple-touch-icon-precomposed'
        sizes='120x120'
        href='/s/modern/apple-touch/common/apple-touch-icon-120x120-precomposed.png'
      />
      <link
        type='image/png'
        rel='apple-touch-icon-precomposed'
        sizes='144x144'
        href='/s/modern/apple-touch/common/apple-touch-icon-144x144-precomposed.png'
      />
      <link
        type='image/png'
        rel='apple-touch-icon-precomposed'
        sizes='152x152'
        href='/s/modern/apple-touch/common/apple-touch-icon-152x152-precomposed.png'
      />
      <link
        type='image/png'
        rel='apple-touch-icon-precomposed'
        sizes='192x192'
        href='/s/modern/apple-touch/common/apple-touch-icon-192x192-precomposed.png'
      />
      <link rel='manifest' href='/s/modern/manifest/manifest.json' />
      <script
        dangerouslySetInnerHTML={{
          __html: extraHtml
        }}
      />
      <noscript>
        <div>
          <img src='https://mc.yandex.ru/watch/17911543' style={{ position: 'absolute', left: '-9999px' }} alt='' />
        </div>
      </noscript>
    </Head>
  );
};

// eslint-disable-next-line complexity
export const Page = (props: PageProps) => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { context, setContext } = useContext(CommonContext);
  useEffect(() => {
    if (!context && props.ctx) {
      setContext(props.ctx);
    }
  }, [context, props.ctx, setContext]);

  useEffect(() => {
    if (context && context.host) {
      saveUtm(context.host);
    }
  }, [context]);

  let backLink = props.backLink;
  if (!backLink) {
    const backUrl: string = (props.ctx.query as { backUrl: string })?.backUrl || '';
    const backName: string = (props.ctx.query as { backUrlName: string })?.backUrlName || '';
    if (
      isSafeUrlParamStrict(backUrl, ['kvartirka.com', 'staging.kvartirka.pro', 'kvtest11.ru']) &&
      backName.length > 0
    ) {
      backLink = {
        href: backUrl,
        text: backName,
        external: false
      };
    }
  }

  return (
    <>
      <SeoHead {...props} />
      {props.renderLayout ? (
        props.renderLayout()
      ) : (
        // Если передаем "props.backLink", то ссылка отображается в хедере на десктопе, и под хедером на мобильном
        <Layout
          mainHost={props.ctx.host as string}
          context={props.ctx}
          backLink={backLink}
          hasSearchBar={props.hasSearchBar}
          searchOptions={props.searchOptions}
          slimFooter={props.slimFooter}
          noFooter={props.noFooter}
          noHeader={props.noHeader}
          isCentral={props.isCentral}
          isTransparentHead={props.isTransparentHead}
          hasMobileSearchBar={props.hasMobileSearchBar}
          headerRef={props.headerRef}
          withoutLoading={props.withoutLoading}
          simpleHeader={props.simpleHeader}
          disableHorizontalMobileScroll={props.disableHorizontalMobileScroll}
          hideCookieAlert={props.ctx.cookies ? props.ctx.cookies?.search(`${COOKIE_HIDE_NAME}=true`) > 0 : false}
          headerClassName={props.headerClassName}
        >
          <>
            {backLink && !props.ctx.isApp && !props.hasMobileSearchBar && (
              <div className={styles.mobileBackLink}>
                <HeaderBackLink {...backLink} />
              </div>
            )}
            {props.children}
          </>
        </Layout>
      )}
    </>
  );
};
